import wootConstants from 'dashboard/constants/globals';
import types from '../mutation-types';
import ConversationApi from '../../api/inbox/conversation';
import {
  getGroupValue,
  GROUP_BY_TYPE,
  NO_VALUE_KEY,
} from 'dashboard/components/widgets/conversation/kanban/helpers';

const { RESOLVED, OPEN } = wootConstants.STATUS_TYPE;

export const statusForStage = (currentStatus, toStage, resolvedStage) => {
  if (!resolvedStage) return null;
  if (toStage === resolvedStage) {
    return currentStatus === RESOLVED ? null : RESOLVED;
  }
  return currentStatus === RESOLVED ? OPEN : null;
};

const conversationQueues = new Map();

const enqueue = (conversationId, task) => {
  const previous = conversationQueues.get(conversationId) || Promise.resolve();
  const run = previous.catch(() => null).then(task);
  conversationQueues.set(conversationId, run);
  const release = () => {
    if (conversationQueues.get(conversationId) === run) {
      conversationQueues.delete(conversationId);
    }
  };
  run.then(release, release);
  return run;
};

const latestConversation = ($getters, conversation) =>
  $getters?.conversationsById?.get(conversation.id) || conversation;

const withStage = (attributes, key, value) => {
  const next = { ...attributes };
  if (value === undefined) {
    delete next[key];
  } else {
    next[key] = value;
  }
  return next;
};

const commitStage = (commit, $getters, conversation, key, value) => {
  const latest = latestConversation($getters, conversation);
  commit(
    types.UPDATE_CONVERSATION_CUSTOM_ATTRIBUTES,
    {
      conversationId: conversation.id,
      customAttributes: withStage(latest.custom_attributes, key, value),
    },
    { root: true }
  );
};

const patchIfUnchanged = (commit, $getters, conversation, change) => {
  const { key, expected, value } = change;
  const onScreen = latestConversation($getters, conversation)
    .custom_attributes?.[key];
  if (onScreen !== expected || value === expected) return;
  commitStage(commit, $getters, conversation, key, value);
};

const commitStatus = (commit, conversationId, status, snoozedUntil) => {
  commit(
    types.CHANGE_CONVERSATION_STATUS,
    { conversationId, status, snoozedUntil },
    { root: true }
  );
};

const planMove = (current, groupBy, toColumnId, resolvedStage) => {
  const isStageMove = groupBy?.type === GROUP_BY_TYPE.CUSTOM_ATTRIBUTE;
  const stageKey = isStageMove ? groupBy.key : null;
  const nextStatus = isStageMove
    ? statusForStage(current.status, toColumnId, resolvedStage)
    : toColumnId;
  return {
    previous: {
      stage: isStageMove ? current.custom_attributes?.[stageKey] : undefined,
      status: current.status,
      snoozedUntil: current.snoozed_until ?? null,
    },
    nextStatus,
    stageKey,
    nextStage: isStageMove ? toColumnId : undefined,
  };
};

const kanbanConflictError = () =>
  Object.assign(new Error('Kanban stage changed elsewhere'), {
    kanbanConflict: true,
  });

const saveMove = async ({ plan, saveStatus, writeStage, progress }) => {
  if (plan.stageKey) {
    const { saved, conflict } = await writeStage(plan.nextStage);
    progress.stageOnServer = saved;
    if (conflict) {
      progress.hasConflict = true;
      throw kanbanConflictError();
    }
    progress.isStageSaved = true;
    if (progress.stageOnServer !== plan.nextStage) {
      throw new Error('Kanban stage was not kept by the server');
    }
  }
  if (plan.nextStatus) {
    await saveStatus();
    progress.isStatusSaved = true;
  }
};

const undoMove = async ({
  commit,
  $getters,
  conversation,
  plan,
  revertStage,
  progress,
}) => {
  const serverHasStage = progress.isStageSaved || progress.hasConflict;
  const stageOnScreen = serverHasStage
    ? progress.stageOnServer
    : plan.nextStage;
  let stageOnServer = serverHasStage
    ? progress.stageOnServer
    : plan.previous.stage;
  if (progress.isStageSaved && !progress.isStatusSaved) {
    stageOnServer = await revertStage(
      plan.previous.stage,
      progress.stageOnServer
    ).catch(() => progress.stageOnServer);
  }
  if (plan.stageKey) {
    patchIfUnchanged(commit, $getters, conversation, {
      key: plan.stageKey,
      expected: stageOnScreen,
      value: stageOnServer,
    });
  }
  const statusOnScreen = latestConversation($getters, conversation).status;
  if (
    plan.nextStatus &&
    !progress.isStatusSaved &&
    statusOnScreen === plan.nextStatus
  ) {
    commitStatus(
      commit,
      conversation.id,
      plan.previous.status,
      plan.previous.snoozedUntil
    );
  }
};

const persistMove = async ({
  commit,
  dispatch,
  $getters,
  conversation,
  plan,
  lockedKeys = [],
}) => {
  const conversationId = conversation.id;
  const saveStatus = () =>
    dispatch(
      'toggleStatus',
      { conversationId, status: plan.nextStatus },
      { root: true }
    );
  let droppedByServer = {};
  const requestStage = async (stage, guard, restore = {}) => {
    const { data } = await ConversationApi.updateCustomAttributes({
      conversationId,
      customAttributes: { ...restore, [plan.stageKey]: stage ?? null },
      merge: true,
      ...guard,
    });
    const dropped = data?.dropped_custom_attributes || {};
    const conflict = Boolean(data?.conflict);
    if (!data?.custom_attributes) return { saved: stage, dropped, conflict };
    const saved = data.custom_attributes[plan.stageKey];
    patchIfUnchanged(commit, $getters, conversation, {
      key: plan.stageKey,
      expected: stage,
      value: saved,
    });
    return { saved, dropped, conflict };
  };
  const writeStage = async stage => {
    const { saved, dropped, conflict } = await requestStage(stage, {
      expected: { [plan.stageKey]: plan.previous.stage ?? null },
    });
    droppedByServer = dropped;
    return { saved, conflict };
  };
  const revertStage = async (stage, stageWeLeft) => {
    const restore = Object.fromEntries(
      Object.entries(droppedByServer).filter(
        ([key]) => !lockedKeys.includes(key)
      )
    );
    const stillMissing = Object.fromEntries(
      Object.keys(restore).map(key => [key, null])
    );
    const { saved } = await requestStage(
      stage,
      { expected: { ...stillMissing, [plan.stageKey]: stageWeLeft ?? null } },
      restore
    );
    return saved;
  };
  const progress = { isStageSaved: false, isStatusSaved: false };

  if (plan.stageKey) {
    commitStage(commit, $getters, conversation, plan.stageKey, plan.nextStage);
  }
  if (plan.nextStatus) {
    commitStatus(commit, conversationId, plan.nextStatus, null);
  }
  try {
    await saveMove({ plan, saveStatus, writeStage, progress });
  } catch (error) {
    await undoMove({
      commit,
      $getters,
      conversation,
      plan,
      revertStage,
      progress,
    });
    throw error;
  }
};

export const moveConversation = (
  { commit, dispatch, getters: $getters, state: $state, rootGetters },
  { conversation, toColumnId, resolvedStage = null }
) => {
  if (!conversation || toColumnId === NO_VALUE_KEY) return Promise.resolve();
  const { groupBy } = $state;
  const lockedKeys =
    rootGetters?.['customAttributePermissions/getNonEditableKeys']?.(
      'conversation_attribute'
    ) || [];

  return enqueue(conversation.id, async () => {
    const current = latestConversation($getters, conversation);
    const fromColumnId = getGroupValue(current, groupBy);
    if (fromColumnId === toColumnId) return;
    const plan = planMove(current, groupBy, toColumnId, resolvedStage);
    await persistMove({
      commit,
      dispatch,
      $getters,
      conversation,
      plan,
      lockedKeys,
    });
    dispatch('refreshColumn', fromColumnId);
  });
};
